import React, { useState, useEffect, useCallback } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  List, 
  ListItem, 
  ListItemText,
  ListItemIcon, 
  Checkbox, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  TextField,
  CircularProgress,
  Divider,
  Box,
  Typography,
  Alert
} from '@mui/material';

import '../../../assets/scss/components/ui/modal/AddVehicleModal.scss';
import { useSchedule } from '../../../hooks/vehicle/useSchedule';

const AddVehicleModal = ({ 
  open, 
  onClose, 
  operationId, 
  selectedDate, 
  onVehicleAdd,
  selectedRouteId = null,
  routeName = null,
  theme = 'light' 
}) => {
  // Estados del componente
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [vehicleType, setVehicleType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Hook personalizado para gestión de datos
  const { useAvailableVehicles } = useSchedule(operationId, selectedRouteId);
  
  // Query optimizada usando el hook personalizado
  const { 
    data: availableVehiclesResponse,
    isLoading,
    error,
    refetch
  } = useAvailableVehicles(selectedDate, {
    vehicleType,
    search: searchTerm,
    enabled: open && !!operationId && !!selectedDate
  });
  
  // Extraer datos con estructura robusta
  const availableVehicles = availableVehiclesResponse?.data || { 
    vehicles: [], 
    combined: [] 
  };
  
  // Reset de estado al abrir modal con limpieza completa
  useEffect(() => {
    if (open) {
      setSelectedVehicles([]);
      setValidationErrors([]);
      setSearchTerm('');
      // El refetch ya está manejado por el hook cuando cambian las dependencias
    }
  }, [open]);
  
  // Reset de selección cuando cambian filtros
  useEffect(() => {
    if (open) {
      setSelectedVehicles([]);
      setValidationErrors([]);
    }
  }, [vehicleType, searchTerm, open]);
  
  // Validación avanzada de selección de vehículos
  const validateVehicleSelection = useCallback((vehicleIds) => {
    const errors = [];
    
    vehicleIds.forEach(vehicleId => {
      // Buscar vehículo en ambas listas
      const allVehicles = [
        ...(availableVehicles.vehicles || []),
        ...(availableVehicles.combined || [])
      ];
      
      const vehicle = allVehicles.find(v => v.id === vehicleId);

      if (!vehicle) {
        errors.push(`Vehículo con ID ${vehicleId} no encontrado`);
        return;
      }

      // Validaciones específicas
      if (vehicle.isScheduled) {
        const vehicleName = vehicle.plate || vehicle.vehiclePlate || `ID: ${vehicleId}`;
        errors.push(`${vehicleName} ya está programado${
          vehicle.scheduledRoutes ? ` en: ${vehicle.scheduledRoutes}` : ''
        }`);
      }

      if (vehicle.hasRouteConflict && selectedRouteId) {
        const vehicleName = vehicle.plate || vehicle.vehiclePlate || `ID: ${vehicleId}`;
        errors.push(`${vehicleName} tiene conflicto con la ruta seleccionada`);
      }

      // Validación de disponibilidad temporal
      if (vehicle.isTemporarilyUnavailable) {
        const vehicleName = vehicle.plate || vehicle.vehiclePlate || `ID: ${vehicleId}`;
        errors.push(`${vehicleName} está temporalmente no disponible`);
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  }, [availableVehicles, selectedRouteId]);

  // Manejo optimizado de selección de vehículos
  const handleVehicleSelect = (vehicleId) => {
    setSelectedVehicles(prev => {
      const newSelection = prev.includes(vehicleId)
        ? prev.filter(id => id !== vehicleId)
        : [...prev, vehicleId];
      
      // Validar nueva selección en tiempo real
      validateVehicleSelection(newSelection);
      
      return newSelection;
    });
  };
  
  // Manejo de cambios en filtros
  const handleTypeChange = (e) => {
    setVehicleType(e.target.value);
  };
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Búsqueda optimizada - el refetch se maneja automáticamente por el hook
  const handleSearchSubmit = () => {
    // El hook se actualiza automáticamente cuando cambia searchTerm
    // No necesitamos refetch manual aquí
  };
  
  // Envío con validación robusta
  const handleSubmit = async () => {
    if (selectedVehicles.length === 0) return;
    
    // Validación final exhaustiva
    if (!validateVehicleSelection(selectedVehicles)) {
      return;
    }

    try {
      await onVehicleAdd(selectedVehicles);
      onClose();
    } catch (error) {
      console.error('Error al añadir vehículos:', error);
      
      // Manejo específico de errores de duplicados
      if (error.isDuplicateError || error.code === 'DUPLICATE_VEHICLE') {
        setValidationErrors([error.message || 'Error: Vehículo duplicado detectado']);
      } else if (error.isValidationError) {
        setValidationErrors(Array.isArray(error.message) ? error.message : [error.message]);
      } else {
        setValidationErrors(['Error inesperado al añadir vehículos. Intente nuevamente.']);
      }
    }
  };
  
  // Renderizado optimizado de elementos de vehículo
  const renderVehicleItem = (vehicle, isCombo = false) => {
    const vehicleId = vehicle.id;
    const displayName = isCombo 
      ? `${vehicle.vehiclePlate} - ${vehicle.trailerPlate}`
      : vehicle.plate;
    
    const isScheduled = vehicle.isScheduled;
    const hasConflict = vehicle.hasRouteConflict;
    const isTemporarilyUnavailable = vehicle.isTemporarilyUnavailable;
    const isDisabled = isScheduled || hasConflict || isTemporarilyUnavailable;

    return (
      <ListItem 
        key={vehicleId}
        button
        onClick={() => !isDisabled && handleVehicleSelect(vehicleId)}
        disabled={isDisabled}
        className={isDisabled ? `GM__${theme}-vehicle-item-disabled` : ''}
        sx={{
          '&:hover': !isDisabled ? {
            backgroundColor: theme === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)'
          } : {}
        }}
      >
        <ListItemIcon>
          <Checkbox
            edge="start"
            checked={selectedVehicles.includes(vehicleId)}
            disabled={isDisabled}
            tabIndex={-1}
          />
        </ListItemIcon>
        <ListItemText 
          primary={
            <Typography variant="body1" component="span">
              {displayName}
            </Typography>
          }
          secondary={
            <Box>
              <Typography variant="body2" color="textSecondary">
                {vehicle.type}
              </Typography>
              
              {/* Indicadores de estado */}
              {isScheduled && (
                <Typography variant="caption" color="warning.main" display="block">
                  Ya programado{vehicle.scheduledRoutes ? ` en: ${vehicle.scheduledRoutes}` : ''}
                </Typography>
              )}
              
              {hasConflict && selectedRouteId && (
                <Typography variant="caption" color="error.main" display="block">
                  Conflicto de ruta
                </Typography>
              )}
              
              {isTemporarilyUnavailable && (
                <Typography variant="caption" color="error.main" display="block">
                  Temporalmente no disponible
                </Typography>
              )}
              
              {vehicle.timesScheduled > 0 && (
                <Typography variant="caption" color="info.main" display="block">
                  Programado {vehicle.timesScheduled} vez(es) esta fecha
                </Typography>
              )}
              
              {vehicle.lastScheduledTime && (
                <Typography variant="caption" color="textSecondary" display="block">
                  Último uso: {new Date(vehicle.lastScheduledTime).toLocaleTimeString()}
                </Typography>
              )}
            </Box>
          }
        />
      </ListItem>
    );
  };
  
  // Utilidad para formateo de fecha
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className={`GM__${theme}-add-vehicle-modal`}
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box>
          <Typography variant="h6" component="div">
            Añadir Vehículos - {selectedDate ? formatDate(selectedDate) : ''}
          </Typography>
          {routeName && selectedRouteId && (
            <Typography variant="subtitle2" color="textSecondary">
              Ruta: {routeName}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Controles de filtrado mejorados */}
        <Box className={`GM__${theme}-filters-container`}>
          <FormControl variant="outlined" fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Tipo de vehículo</InputLabel>
            <Select
              value={vehicleType}
              onChange={handleTypeChange}
              label="Tipo de vehículo"
            >
              <MenuItem value="all">Todos los tipos</MenuItem>
              <MenuItem value="vehicles">Solo vehículos individuales</MenuItem>
              <MenuItem value="combined">Solo combinaciones</MenuItem>
            </Select>
          </FormControl>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              label="Buscar vehículo"
              variant="outlined"
              fullWidth
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearchSubmit();
              }}
              placeholder="Buscar por placa, tipo o ID..."
            />
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleSearchSubmit}
              disabled={isLoading}
              sx={{ minWidth: '80px' }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Buscar'}
            </Button>
          </Box>
        </Box>
        
        {/* Alertas de validación */}
        {validationErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Advertencias de validación:
            </Typography>
            <Box component="ul" sx={{ margin: 0, paddingLeft: '20px' }}>
              {validationErrors.map((error, index) => (
                <Typography component="li" key={index} variant="body2">
                  {error}
                </Typography>
              ))}
            </Box>
          </Alert>
        )}
        
        <Divider sx={{ my: 1 }} />
        
        {/* Contenido principal */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Cargando vehículos disponibles...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ color: 'error.main', p: 2, textAlign: 'center' }}>
            <Typography variant="body1" gutterBottom>
              Error al cargar vehículos
            </Typography>
            <Typography variant="body2">
              {error.message || 'Error desconocido. Intente nuevamente.'}
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={refetch}
              sx={{ mt: 1 }}
            >
              Reintentar
            </Button>
          </Box>
        ) : (
          <>
            {/* Vehículos individuales */}
            {availableVehicles.vehicles && availableVehicles.vehicles.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', my: 1 }}>
                  Vehículos Individuales ({availableVehicles.vehicles.length})
                </Typography>
                <List dense>
                  {availableVehicles.vehicles.map(vehicle => 
                    renderVehicleItem(vehicle, false)
                  )}
                </List>
              </>
            )}
            
            {/* Combinaciones de vehículos */}
            {availableVehicles.combined && availableVehicles.combined.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', my: 1 }}>
                  Combinaciones ({availableVehicles.combined.length})
                </Typography>
                <List dense>
                  {availableVehicles.combined.map(combo => 
                    renderVehicleItem(combo, true)
                  )}
                </List>
              </>
            )}
            
            {/* Estado vacío */}
            {availableVehiclesResponse && 
             (!availableVehicles.vehicles || availableVehicles.vehicles.length === 0) && 
             (!availableVehicles.combined || availableVehicles.combined.length === 0) && (
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  No hay vehículos disponibles
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {searchTerm && `para la búsqueda "${searchTerm}"`}
                  {selectedRouteId && !searchTerm && 'para la ruta seleccionada'}
                  {!searchTerm && !selectedRouteId && 'en esta fecha'}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" size="large">
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          size="large"
          disabled={
            selectedVehicles.length === 0 || 
            isLoading || 
            validationErrors.length > 0
          }
          sx={{ minWidth: '120px' }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            `Añadir (${selectedVehicles.length})`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddVehicleModal;